import {css, CSSResult, html, LitElement, nothing, unsafeCSS,} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {consume} from '@lit/context';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import styles from './share-collection-layer.scss';
import sharedIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_eye.svg';
import privateIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_eye-closed.svg';
import copyIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_copy.svg';
import checkmarkIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-16_check.svg';
import {CollectionTrackingContext, Messages, WishlistCollection} from '../../../@types/wishlist.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {WishlistAppConfig, wishlistAppConfigContext} from '../../../scripts/context/appConfigContext.js';
import {collectionApi} from '../../../scripts/service/collection-api.js';
import {collectionsContext} from '../../../scripts/context/wishlistContext.js';
import {CollectionsState} from '../../../scripts/context/wishlist-state.js';
import {pushToSnackbar} from '../../../scripts/service/snackbar-service.js';
import {trackShareCollection} from '../../../scripts/service/cube-tracking-layer.js';
import {collectionMenuContext} from '../../../scripts/context/menuContext.js';
import {CollectionMenuState} from '../../../scripts/context/menu-state.js';
import {hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';
import {getBackButton} from '../base-layer/base-layer.js';
import baseLayerStyles from '../base-layer/base-layer.scss';

@customElement('ents-share-collection-layer')
export class ShareCollectionLayer extends LitElement {
  @consume({context: i18nContext})
  private messages!: Messages;

  @consume({context: wishlistAppConfigContext})
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({context: collectionMenuContext, subscribe: true})
  @property({attribute: false})
  private actionMenuState!: CollectionMenuState;

  @consume({context: collectionsContext, subscribe: true})
  @property({attribute: false})
  private collectionsState!: CollectionsState;

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`, css`${unsafeCSS(baseLayerStyles)}`];
  }

  private backButtonAction() {
    if (this.actionMenuState.context.backButtonCallback) {
      this.actionMenuState.context.backButtonCallback();
    }
    this.closeLayer();
    this.dispatchEvent(new Event('ents-layer-back', {composed: true, bubbles: true}));
  }

  private closeLayer = () => {
    if (this.actionMenuState.context.openShareView) {
      this.actionMenuState.closeShareView();
    }
  };

  private async updateIsSharingAllowed(isSharingAllowed: boolean) {
    const collection = {
      ...this.actionMenuState.context.collection!,
      isSharingAllowed: isSharingAllowed
    };
    const result = await collectionApi.update(this.wishlistAppConfig.collectionEndpoint, collection);
    if (result.isOk) {
      this.collectionsState.updateOnIsSharingAllowed(
        result.collection!,
        this.actionMenuState.context.collectionTrackingContext!
      );
      this.actionMenuState.update({collection: result.collection});
    }
  }

  private async copyLink(
    shareUrl: string,
    collection: WishlistCollection | null,
    collectionTrackingContext: CollectionTrackingContext | null,
    shouldCloseLayer: boolean = true
  ) {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(shareUrl);
      trackShareCollection(collection!, collectionTrackingContext!);
      if (shouldCloseLayer) {
        this.closeLayer();
      }
      pushToSnackbar(this.messages['ents_wishlist_collection_share_snackbar_title']!);
    }
  }

  private async share(
    itemInfoToShare: { url: string, title?: string, text?: string },
    collection: WishlistCollection | null,
    collectionTrackingContext: CollectionTrackingContext | null
  ) {
    if (navigator.share !== undefined && navigator.canShare(itemInfoToShare)) {
      await navigator.share(itemInfoToShare);
      trackShareCollection(collection!, collectionTrackingContext!);
      this.closeLayer();
    }
  }

  private renderShareContent() {
    const collection = this.actionMenuState.context.collection;
    const collectionTrackingContext = this.actionMenuState.context.collectionTrackingContext;
    const shareUrl = `https://www.breuninger.com${this.wishlistAppConfig.collectionEndpoint}${collection?.id}/`;
    const itemInfoToShare = {
      url: shareUrl,
      title: this.messages['ents_wishlist_sharing_collection_title'],
      text: `${this.messages['ents_wishlist_sharing_collection_body']}`.replace('{}', collection?.name as string),
    };

    const renderShareContent = () => {
      if (navigator.share !== undefined && navigator.canShare(itemInfoToShare)) {
        return html`
          <button
            class="bdk-button" type="button" variant="outline" size="medium"
            @click="${() => this.share(itemInfoToShare, collection, collectionTrackingContext)}"
            data-test-ents-wishlist-collection-share-button
          >
            ${this.messages['ents_wishlist_collection_share_button']}
          </button>
        `;
      }
      if (navigator.clipboard) {
        return html`
          <button
            class="bdk-button" type="button" variant="outline" size="medium"
            @click="${() => this.copyLink(shareUrl, collection, collectionTrackingContext, true)}"
            data-test-ents-wishlist-collection-share-button
            id="copy-shared-list-button"
          >
            ${hideIconFromAssistiveTechnology(copyIcon())}
            ${this.messages['ents_wishlist_collection_share_copy_button']}
          </button>
        `;
      }
      return nothing;
    };

    return html`
      <div>
        <div class="bdk-text-field"
             @click="${() => this.copyLink(shareUrl, collection, collectionTrackingContext, false)}">
          <div class="bdk-text-field--container">
            <input
              class="bdk-text-field--input"
              type="text"
              value="${shareUrl}"
              readonly
              disabled
              data-test-ents-wishlist-collection-share-link
              @click="${() => this.copyLink(shareUrl, collection, collectionTrackingContext, false)}"
            >
            <div class="bdk-text-field--icons">
              <button type="button"
                      aria-label="${this.messages['ents_wishlist_collection_share_copy_button']}"
                      @click="${() => this.copyLink(shareUrl, collection, collectionTrackingContext, false)}">
                ${hideIconFromAssistiveTechnology(copyIcon())}
              </button>
            </div>
          </div>
        </div>
        ${renderShareContent()}
      </div>
    `;
  }

  protected render() {
    const collection = this.actionMenuState.context.collection;
    return html`
      <ents-base-layer
        drawer-test-id="ents-share-collection-layer"
        ?showLayer="${this.actionMenuState.context.openShareView}"
        @ents-layer-closed="${this.closeLayer}"
      >
        ${this.actionMenuState.context.backButtonCallback != undefined ?
          getBackButton(this.messages, 'ents-share-collection-layer', this.backButtonAction.bind(this)) : nothing}
        <div class="ents-share-collection-layer" data-test-ents-wishlist-share-collection-layer>
          <h2 slot="title">${this.messages['ents_wishlist_collection_share_title']}</h2>
          <p class="ents-copy">${this.messages['ents_wishlist_collection_share_subtitle']}</p>

          <div class="ents-share-collection-layer__toggle-wrapper">
            <label class="ents-share-collection-layer__edit-share" for="ents-share-collection-layer__toggle">
              <span class="ents-share-collection-layer__switch--label">
                ${collection?.isSharingAllowed ?
                  hideIconFromAssistiveTechnology(sharedIcon()) :
                  hideIconFromAssistiveTechnology(privateIcon())}
                ${this.messages['ents_wishlist_collection_share_label']}
              </span>
              <span class="ents-share-collection-layer__switch" data-test-ents-wishlist-collection-share-switch>
                <input
                  id="ents-share-collection-layer__toggle"
                  type="checkbox"
                  .checked="${collection?.isSharingAllowed}"
                  @click="${() => this.updateIsSharingAllowed(!collection?.isSharingAllowed)}"
                >
                <span class="ents-share-collection-layer__switch--slider">
                  ${hideIconFromAssistiveTechnology(checkmarkIcon())}
                </span>
              </span>
            </label>
            ${collection?.isSharingAllowed ? this.renderShareContent() : nothing}
          </div>

          <wishlist-alert>
            <p class="ents-copy-small-bold">
              ${this.messages['ents_wishlist_collection_share_hint']}
            </p>
          </wishlist-alert>
        </div>
      </ents-base-layer>`;
  }
}
